/**
 * SignatureService.js
 * Serviço de Orquestração de Assinaturas (Real Clicksign API + Firestore Lifecycle)
 */

import { useEffect, useState } from 'react';
import { addDoc, collection, doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { FS } from '../shared/constants/firestorePaths';
import { generateDocumentFileStem } from '../utils/documentUtils';

const appId = FIREBASE_CONFIG.projectId;

// ── Firestore path helper ──────────────────────────────────────────────────
const wsRef = (workspaceId) =>
  doc(db, ...FS.autosWorkspaces(appId), workspaceId);

// ── SIGNATURE_STATUS enum ──────────────────────────────────────────────────
export const SIGNATURE_STATUS = {
  NONE:              'NONE',
  PENDING_GOVBR:     'PENDING_GOVBR',
  PENDING_CLICKSIGN: 'PENDING_CLICKSIGN',
  REVIEW_PENDING:    'REVIEW_PENDING',
  SIGNED:            'SIGNED',
};

// ── requestDocumentSignature ───────────────────────────────────────────────
// method: 'GOVBR' | 'CLICKSIGN'
export async function requestDocumentSignature(workspaceId, slotId, method, link = null) {
  const wid = String(workspaceId || '').trim();
  const sid = String(slotId     || '').trim();
  if (!wid || !sid || !appId) return;
  const status = method === 'CLICKSIGN'
    ? SIGNATURE_STATUS.PENDING_CLICKSIGN
    : SIGNATURE_STATUS.PENDING_GOVBR;
  try {
    await setDoc(wsRef(wid), {
      signatureRequests: {
        [sid]: {
          status,
          method:      String(method || 'GOVBR'),
          link:        link ? String(link) : null,
          requestedAt: Date.now(),
          signedAt:    null,
        },
      },
    }, { merge: true });
  } catch {}
}

// ── updateSignatureStatus ──────────────────────────────────────────────────
export async function updateSignatureStatus(workspaceId, slotId, status) {
  const wid = String(workspaceId || '').trim();
  const sid = String(slotId     || '').trim();
  if (!wid || !sid || !appId) return;
  try {
    const patch = { status: String(status) };
    if (status === SIGNATURE_STATUS.SIGNED) patch.signedAt = Date.now();
    await setDoc(wsRef(wid), {
      signatureRequests: { [sid]: patch },
    }, { merge: true });
  } catch {}
}

// ── createClientSignatureNotification ────────────────────────────────────
// Writes a SIGNATURE_REQUEST notification to each client linked to the workspace.
export async function createClientSignatureNotification(workspaceId, slotLabel, method) {
  const wid = String(workspaceId || '').trim();
  if (!wid || !appId) return;
  try {
    const wsSnap = await getDoc(wsRef(wid));
    if (!wsSnap.exists()) return;
    const partiesRefs = wsSnap.data()?.partiesRefs || [];
    const clients = partiesRefs.filter((p) => String(p?.clientId || '').trim());
    if (!clients.length) return;
    const via = method === 'CLICKSIGN' ? 'Clicksign' : 'Gov.br';
    const label = String(slotLabel || 'Documento').slice(0, 80);
    for (const party of clients) {
      const clientId = String(party.clientId).trim();
      const notifCol = collection(db, ...FS.clientNotifications(appId, clientId));
      await addDoc(notifCol, {
        title:       'Assinatura Solicitada',
        message:     `O documento "${label}" requer sua assinatura via ${via}.`,
        type:        'SIGNATURE_REQUEST',
        slotLabel:   label,
        method,
        workspaceId: wid,
        read:        false,
        createdAt:   Date.now(),
      });
    }
  } catch {}
}

// ── markClientSignatureNotificationsRead ──────────────────────────────────
// Marks all SIGNATURE_REQUEST notifications for a workspace as read (MC4).
export async function markClientSignatureNotificationsRead(clientId, workspaceId) {
  const cid = String(clientId   || '').trim();
  const wid = String(workspaceId || '').trim();
  if (!cid || !wid || !appId) return;
  try {
    const { getDocs, query: fsQuery, where, updateDoc } = await import('firebase/firestore');
    const notifCol = collection(db, ...FS.clientNotifications(appId, cid));
    const snap = await getDocs(fsQuery(notifCol, where('workspaceId', '==', wid), where('read', '==', false)));
    const batch = (await import('firebase/firestore')).writeBatch(db);
    snap.docs.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();
  } catch {}
}

// ── useWorkspaceSignatures ─────────────────────────────────────────────────
// Returns { signatures: { [slotId]: { status, method, link, requestedAt, signedAt } } }
export function useWorkspaceSignatures(workspaceId) {
  const [signatures, setSignatures] = useState({});

  useEffect(() => {
    const wid = String(workspaceId || '').trim();
    if (!wid || !appId) { setSignatures({}); return; }
    const unsub = onSnapshot(wsRef(wid), (snap) => {
      setSignatures(snap.exists() ? (snap.data()?.signatureRequests || {}) : {});
    }, () => setSignatures({}));
    return () => unsub();
  }, [workspaceId]);

  return { signatures };
}

const CLICKSIGN_API = 'https://app.clicksign.com/api/v1'; // Produção
// Para Sandbox use: 'https://sandbox.clicksign.com/api/v1'

const SignatureService = {

  _getClicksignToken() {
    try {
      if (typeof import.meta !== 'undefined' && import.meta?.env?.VITE_CLICKSIGN_TOKEN) {
        return import.meta.env.VITE_CLICKSIGN_TOKEN;
      }
    } catch (_) {
      // ignore
    }
    try {
      if (typeof process !== 'undefined' && process?.env?.REACT_APP_CLICKSIGN_TOKEN) {
        return process.env.REACT_APP_CLICKSIGN_TOKEN;
      }
    } catch (_) {
      // ignore
    }
    return '';
  },

  // Helper para limpar e formatar telefone (Evita erro 400 da API)
  _sanitizePhone(phone) {
    if (!phone) return "";
    // Remove tudo que não é número
    let clean = String(phone).replace(/\D/g, '');

    // Se o número tiver 10 ou 11 dígitos (ex: 41999999999), assume que é BR e adiciona 55
    if (clean.length >= 10 && clean.length <= 11) {
      clean = '55' + clean;
    }

    // A Clicksign exige o "+" na frente
    return '+' + clean;
  },
  
  // FLUXO CLICKSIGN REAL (Cadeia de Promessas)
  async sendContractToClicksign(contractData, pdfBase64) {
    const API_TOKEN = this._getClicksignToken();
    
    if (!API_TOKEN) {
      return { success: false, error: "Token da API não configurado no .env.local (VITE_CLICKSIGN_TOKEN)." };
    }

    if (!pdfBase64) {
      return { success: false, error: "PDF Base64 vazio." };
    }

    // Prepara dados sanitizados
    const safePhone = this._sanitizePhone(contractData.clientPhone);
    const safeName = contractData.clientName || "Cliente";
    const documentStem = generateDocumentFileStem('Contrato', { name: safeName }, {
      mainPartyName: safeName,
      ramoDireito: contractData?.ramoDireito || ''
    });

    if (safePhone.length < 12) {
      console.warn("Telefone parece inválido mesmo após sanitização:", safePhone);
    }

    try {
      // 1. UPLOAD DO DOCUMENTO
      const docBody = {
        document: {
          path: `/Contratos/${documentStem}.pdf`,
          content_base64: `data:application/pdf;base64,${pdfBase64}`,
          deadline_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 dias
          auto_close: true,
          locale: "pt-BR"
        }
      };

      const docRes = await fetch(`${CLICKSIGN_API}/documents?access_token=${API_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(docBody)
      });
      
      if (!docRes.ok) {
        const errTxt = await docRes.text();
        throw new Error(`Erro Upload (${docRes.status}): ${errTxt}`);
      }
      const docJson = await docRes.json();
      const documentKey = docJson.document.key;

      // 2. CRIAR/BUSCAR SIGNATÁRIO
      const signerBody = {
        signer: {
          email: contractData.clientEmail,
          phone_number: safePhone, // Telefone formatado (+55...)
          auths: ["whatsapp"], // Autenticação via WhatsApp
          name: safeName,
          has_documentation: true // Exige documentoscopia/selfie se configurado na conta
        }
      };

      const signerRes = await fetch(`${CLICKSIGN_API}/signers?access_token=${API_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(signerBody)
      });

      if (!signerRes.ok) {
        const errTxt = await signerRes.text();
        throw new Error(`Erro Signer (${signerRes.status}): ${errTxt}`);
      }
      const signerJson = await signerRes.json();
      const signerKey = signerJson.signer.key;

      // 3. VINCULAR SIGNATÁRIO AO DOCUMENTO (LIST)
      const listBody = {
        list: {
          document_key: documentKey,
          signer_key: signerKey,
          sign_as: "contractor", // ou 'party'
          message: "Olá! Segue seu contrato de honorários da Rodrigo Demeterco Advocacia para assinatura digital segura."
        }
      };

      const listRes = await fetch(`${CLICKSIGN_API}/lists?access_token=${API_TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(listBody)
      });

      if (!listRes.ok) {
        const errTxt = await listRes.text();
        throw new Error(`Erro List (${listRes.status}): ${errTxt}`);
      }

      return {
        success: true,
        status: "sent",
        message: "Contrato enviado com sucesso para o WhatsApp!",
        documentKey: documentKey
      };

    } catch (error) {
      console.error("❌ Erro Clicksign:", error);

      // Detecção de CORS ou Erro de Rede
      if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
        return {
          success: false,
          error: "Bloqueio de Segurança (CORS): A API da Clicksign não aceita envio direto do navegador. Precisaremos mover esta lógica para o Backend (LegalCore)."
        };
      }

      return { success: false, error: error.message };
    }
  },

  getGovBrInstructions() {
    return {
      title: "Instruções Obrigatórias para Assinatura Gov.br",
      steps: [
        "1. Clique no botão abaixo para baixar o PDF da Procuração.",
        "2. Acesse o portal oficial: https://assinador.iti.br",
        "3. Faça login com sua conta Gov.br (Nível Prata ou Ouro).",
        "4. Faça o upload do arquivo PDF.",
        "5. Posicione a assinatura e confirme.",
        "6. Baixe o arquivo assinado e nos envie pelo WhatsApp."
      ],
      portalLink: "https://assinador.iti.br"
    };
  }
};

export { SignatureService };
export default SignatureService;